/**
 * The realistic sheet.
 *
 * The aerodrome plan is a survey drawing: linework on paper. This paints the
 * same georeferenced sheet as the airfield would look from above — sand, the
 * runways in dark asphalt with their white markings, the taxiways in asphalt
 * with yellow centrelines and edge markings, the aprons and stands in concrete,
 * the holding positions, the signs, the navaids — and, at night, the lighting:
 * white runway edge and centreline, green thresholds, blue taxiway edges,
 * green taxiway centrelines, the PAPI, the approach lighting with its
 * sequenced flashers, and the apron floodlights.
 *
 * Everything is placed from the records. The runways are the surveyed axes;
 * the runway lights are the inventory; the taxiway and apron surfaces and the
 * taxiway lights are derived once from the survey overlays by
 * scripts/build-pavement.mjs and shipped as pavement.png and agl-derived.json;
 * the approach lighting, the holding-position markings and the parked
 * aircraft are laid out to the standard pattern from the survey positions and
 * are marked derived. Nothing here changes a coordinate: the picks, the work
 * orders and the teams all land where the drawing puts them.
 *
 * `paintReal` paints in sheet space — the caller has already translated,
 * rotated and scaled — and returns false until the derived assets have
 * loaded, so the drawing falls back to the scan for the first second.
 */
import derived from "../../data/agl-derived.json";
import { ASSETS, DATA } from "./tables";
import { ovTaxi } from "./mapHost";

/* eslint-disable @typescript-eslint/no-explicit-any -- the demo's tables are untyped */
type Rec = any;

export type RealMode = "off" | "day" | "night";

const T = DATA.transform;
const uE = (e: number) => T.u_of_E.a + T.u_of_E.b * e;
const vN = (n: number) => T.v_of_N.a + T.v_of_N.b * n;

/** The derived pavement, tinted once per mode. */
interface Pavement {
  w: number;
  h: number;
  taxi: HTMLCanvasElement;
  apron: HTMLCanvasElement;
  centre: HTMLCanvasElement;
}

let pavementImg: HTMLImageElement | null = null;
let tinted: Record<"day" | "night", Pavement | null> = { day: null, night: null };
let loading: Promise<void> | null = null;
let noise: HTMLCanvasElement | null = null;

const PALETTE = {
  day: { ground: "#C9B48C", asphalt: "#35383B", runway: "#2A2D30", concrete: "#B8B9B3", joint: "rgba(90,92,88,.35)", centre: "#E8B824", white: "#EDEDE8", yellow: "#F2C230", shoulder: "#4A4C4A" },
  night: { ground: "#07090B", asphalt: "#111315", runway: "#0D0F11", concrete: "#171A1C", joint: "rgba(0,0,0,0)", centre: "#5A4A12", white: "rgba(220,220,210,.30)", yellow: "rgba(242,194,48,.35)", shoulder: "#141617" },
};

/** Load pavement.png once; build the three tinted layers for each mode. */
export function loadReal(): Promise<void> {
  if (loading) return loading;
  loading = new Promise<void>((res, rej) => {
    const i = new Image();
    i.onload = () => {
      pavementImg = i;
      res();
    };
    i.onerror = () => rej(new Error("could not load /assets/pavement.png"));
    i.src = `/assets/pavement.png`;
  });
  return loading;
}

export const realReady = () => pavementImg != null;

function tint(mode: "day" | "night"): Pavement | null {
  if (!pavementImg) return null;
  if (tinted[mode]) return tinted[mode];
  const w = pavementImg.width;
  const h = pavementImg.height;
  const src = document.createElement("canvas");
  src.width = w;
  src.height = h;
  const g = src.getContext("2d", { willReadFrequently: true });
  if (!g) return null;
  g.drawImage(pavementImg, 0, 0);
  const d = g.getImageData(0, 0, w, h).data;
  const pal = PALETTE[mode];
  const make = (channel: number, colour: string, alphaScale: number) => {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const cg = c.getContext("2d")!;
    const im = cg.createImageData(w, h);
    const [r, gg, b] = hex(colour);
    for (let j = 0; j < w * h; j++) {
      const a = d[j * 4 + channel];
      if (!a) continue;
      im.data[j * 4] = r;
      im.data[j * 4 + 1] = gg;
      im.data[j * 4 + 2] = b;
      im.data[j * 4 + 3] = Math.round(a * alphaScale);
    }
    cg.putImageData(im, 0, 0);
    return c;
  };
  tinted = { ...tinted, [mode]: { w, h, taxi: make(0, pal.asphalt, 1), apron: make(1, pal.concrete, 1), centre: make(2, pal.centre, mode === "day" ? 1 : 0.9) } };
  return tinted[mode];
}

function hex(c: string): [number, number, number] {
  const m = /^#([0-9a-f]{6})$/i.exec(c);
  if (!m) return [128, 128, 128];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** A small grain, tiled, so the sand and the asphalt are not flat colour. */
function grain(): HTMLCanvasElement {
  if (noise) return noise;
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 128;
  const g = c.getContext("2d")!;
  const im = g.createImageData(128, 128);
  let s = 7;
  for (let j = 0; j < 128 * 128; j++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const v = (s >> 8) & 255;
    im.data[j * 4] = im.data[j * 4 + 1] = im.data[j * 4 + 2] = v;
    im.data[j * 4 + 3] = 22;
  }
  g.putImageData(im, 0, 0);
  noise = c;
  return c;
}

/* ── geometry ────────────────────────────────────────────────────────────── */

export interface RunwayGeom {
  name: string;
  a: [number, number];
  b: [number, number];
  w: number;
  len: number;
  /** unit vector a→b, in metres */
  ux: number;
  uy: number;
  /** threshold offsets by designator */
  thr: Record<string, number>;
}

export function runways(): RunwayGeom[] {
  return ((DATA.runways || []) as Rec[]).map((r) => {
    const [ax, ay] = r.end_a;
    const [bx, by] = r.end_b;
    const len = Math.hypot(bx - ax, by - ay);
    return { name: r.designators, a: [ax, ay], b: [bx, by], w: r.width_m || 45, len, ux: (bx - ax) / len, uy: (by - ay) / len, thr: r.threshold_offset_m || {} };
  });
}

/** The standard runway markings, in metres along and across the axis from end a. */
export interface Marking {
  kind: "edge" | "centre" | "threshold" | "aiming" | "tdz" | "designator";
  /** along, across, length along, width across — all metres; across is from the centreline */
  s: number;
  t: number;
  l: number;
  w: number;
  text?: string;
  /** which end the marking faces: "a" reads from end a */
  end?: "a" | "b";
}

export function runwayMarkings(r: RunwayGeom): Marking[] {
  const out: Marking[] = [];
  const half = r.w / 2;
  out.push({ kind: "edge", s: 0, t: half - 0.45, l: r.len, w: 0.9 }, { kind: "edge", s: 0, t: -half + 0.45, l: r.len, w: 0.9 });
  for (let s = 0; s + 30 < r.len; s += 50) out.push({ kind: "centre", s, t: 0, l: 30, w: 0.9 });
  const [da, db] = r.name.split("/");
  const ends: ["a" | "b", string][] = [
    ["a", da],
    ["b", db],
  ];
  for (const [end, des] of ends) {
    const off = r.thr[des] ?? 0;
    const at = (s: number) => (end === "a" ? off + s : r.len - off - s);
    /* threshold stripes: 12 across a 45 m runway, 16 across 60 */
    const n = r.w >= 55 ? 16 : 12;
    const stripeW = 1.8;
    const span = (n / 2) * (stripeW * 2) - stripeW;
    for (let i = 0; i < n / 2; i++) {
      const t = 1.8 + i * stripeW * 2 + stripeW / 2;
      void span;
      out.push({ kind: "threshold", s: at(6), t, l: 30, w: stripeW, end }, { kind: "threshold", s: at(6), t: -t, l: 30, w: stripeW, end });
    }
    out.push({ kind: "designator", s: at(48), t: 0, l: 9, w: 9, text: des, end });
    out.push({ kind: "aiming", s: at(400), t: 9, l: 45, w: 6, end }, { kind: "aiming", s: at(400), t: -9, l: 45, w: 6, end });
    for (const [d, bars] of [
      [150, 3],
      [300, 2],
      [450, 2],
      [600, 1],
      [750, 1],
      [900, 1],
    ] as [number, number][]) {
      for (let k = 0; k < bars; k++) {
        const t = 12 + k * 3;
        out.push({ kind: "tdz", s: at(d), t, l: 22.5, w: 1.8, end }, { kind: "tdz", s: at(d), t: -t, l: 22.5, w: 1.8, end });
      }
    }
  }
  return out;
}

/** The approach lighting for one end, laid out to the standard pattern: a 900 m centreline of barrettes with a crossbar at 300 m and red side rows inside 270 m. Derived. */
export interface Light {
  E: number;
  N: number;
  colour: string;
  /** flasher sequence index, if a sequenced flasher */
  seq?: number;
  size?: number;
}

export function approachLights(r: RunwayGeom, end: "a" | "b"): Light[] {
  const des = r.name.split("/")[end === "a" ? 0 : 1];
  const off = r.thr[des] ?? 0;
  /* the threshold point, and the outward direction */
  const sign = end === "a" ? -1 : 1;
  const tx = end === "a" ? r.a[0] + r.ux * off : r.b[0] - r.ux * off;
  const ty = end === "a" ? r.a[1] + r.uy * off : r.b[1] - r.uy * off;
  const ox = r.ux * sign;
  const oy = r.uy * sign;
  const px = -r.uy;
  const py = r.ux;
  const out: Light[] = [];
  const at = (s: number, t: number): [number, number] => [tx + ox * s + px * t, ty + oy * s + py * t];
  for (let s = 30; s <= 900; s += 30) {
    const seq = s >= 300 ? (900 - s) / 30 : undefined;
    for (let k = -2; k <= 2; k++) {
      const [E, N] = at(s, k * 1.5);
      out.push({ E, N, colour: "#FFF4D6", seq: k === 0 ? seq : undefined });
    }
    if (s <= 270) for (const side of [-1, 1]) for (let k = 0; k < 3; k++) { const [E, N] = at(s, side * (7 + k * 1.5)); out.push({ E, N, colour: "#FF4030" }); }
  }
  for (let t = -15; t <= 15; t += 1.5) { const [E, N] = at(300, t); out.push({ E, N, colour: "#FFF4D6" }); }
  /* the threshold: green across the full width */
  for (let t = -r.w / 2; t <= r.w / 2; t += 3) { const [E, N] = at(0, t); out.push({ E, N, colour: "#3FE07A", size: 1.2 }); }
  return out;
}

/** Holding-position markings: two solid and two broken lines across the taxiway, drawn along the runway direction at each surveyed holding position. Derived. */
export function holdBar(hold: Rec, rws: RunwayGeom[]): { E: number; N: number; ux: number; uy: number } | null {
  let best: RunwayGeom | null = null;
  let bd = Infinity;
  for (const r of rws) {
    const d = Math.abs((hold.E - r.a[0]) * -r.uy + (hold.N - r.a[1]) * r.ux);
    if (d < bd) { bd = d; best = r; }
  }
  if (!best) return null;
  return { E: hold.E, N: hold.N, ux: best.ux, uy: best.uy };
}

/* ── painting ────────────────────────────────────────────────────────────── */

/**
 * Paint the sheet at W×H sheet pixels. Returns false when the derived
 * pavement is not loaded yet, so the caller draws the scan instead.
 */
export function paintReal(ctx: CanvasRenderingContext2D, W: number, H: number, mode: "day" | "night", nowMs: number): boolean {
  const pav = tint(mode);
  if (!pav) return false;
  const pal = PALETTE[mode];
  const pxPerM = W / (DATA.extent.E_max - DATA.extent.E_min);
  const S = (E: number, N: number): [number, number] => [uE(E) * W, vN(N) * H];
  /* what is on screen, in sheet pixels, from the transform the caller set */
  const inv = ctx.getTransform().inverse();
  const cw = ctx.canvas.width;
  const ch = ctx.canvas.height;
  const corners = [inv.transformPoint({ x: 0, y: 0 }), inv.transformPoint({ x: cw, y: 0 }), inv.transformPoint({ x: 0, y: ch }), inv.transformPoint({ x: cw, y: ch })];
  const vx0 = Math.min(...corners.map((c) => c.x)) - 40;
  const vx1 = Math.max(...corners.map((c) => c.x)) + 40;
  const vy0 = Math.min(...corners.map((c) => c.y)) - 40;
  const vy1 = Math.max(...corners.map((c) => c.y)) + 40;
  const onScreen = (x: number, y: number) => x >= vx0 && x <= vx1 && y >= vy0 && y <= vy1;

  /* ground: the desert runs to the edge of the screen, not the edge of the sheet */
  ctx.fillStyle = pal.ground;
  ctx.fillRect(vx0, vy0, vx1 - vx0, vy1 - vy0);
  if (mode === "day") {
    ctx.save();
    ctx.globalAlpha = 0.55;
    const pat = ctx.createPattern(grain(), "repeat");
    if (pat) { ctx.fillStyle = pat; ctx.fillRect(vx0, vy0, vx1 - vx0, vy1 - vy0); }
    ctx.restore();
  }

  /* pavements from the derived mask */
  ctx.drawImage(pav.apron, 0, 0, W, H);
  ctx.drawImage(pav.taxi, 0, 0, W, H);
  const rws = runways();

  /* runways: asphalt, rubber in the touchdown zones, then the markings */
  for (const r of rws) {
    const [ax, ay] = S(r.a[0], r.a[1]);
    const [bx, by] = S(r.b[0], r.b[1]);
    ctx.save();
    ctx.lineCap = "butt";
    ctx.strokeStyle = pal.shoulder;
    ctx.lineWidth = (r.w + 14) * pxPerM;
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
    ctx.strokeStyle = pal.runway;
    ctx.lineWidth = r.w * pxPerM;
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
    /* rubber: darker over the first 900 m from each threshold, heaviest at 300 m */
    if (mode === "day") {
      for (const end of ["a", "b"] as const) {
        const des = r.name.split("/")[end === "a" ? 0 : 1];
        const off = r.thr[des] ?? 0;
        const sgn = end === "a" ? 1 : -1;
        const sx = (end === "a" ? r.a[0] : r.b[0]) + r.ux * off * sgn;
        const sy = (end === "a" ? r.a[1] : r.b[1]) + r.uy * off * sgn;
        const [x0, y0] = S(sx + r.ux * 120 * sgn, sy + r.uy * 120 * sgn);
        const [x1, y1] = S(sx + r.ux * 950 * sgn, sy + r.uy * 950 * sgn);
        const gr = ctx.createLinearGradient(x0, y0, x1, y1);
        gr.addColorStop(0, "rgba(10,10,12,0)"); gr.addColorStop(0.25, "rgba(10,10,12,.55)"); gr.addColorStop(1, "rgba(10,10,12,0)");
        ctx.strokeStyle = gr; ctx.lineWidth = r.w * 0.5 * pxPerM;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      }
    }
    ctx.restore();
  }
  if (pxPerM > 0.06) {
    ctx.save();
    ctx.fillStyle = pal.white;
    for (const r of rws) {
      const px = -r.uy;
      const py = r.ux;
      for (const m of runwayMarkings(r)) {
        const s0 = m.kind === "edge" || m.kind === "centre" ? m.s : m.end === "a" ? m.s : m.s - m.l;
        const cx = r.a[0] + r.ux * (s0 + m.l / 2) + px * m.t;
        const cy = r.a[1] + r.uy * (s0 + m.l / 2) + py * m.t;
        const [x, y] = S(cx, cy);
        if (!onScreen(x, y)) continue;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.atan2(vN(cy + r.uy) * H - y, uE(cx + r.ux) * W - x));
        if (m.kind === "designator") {
          ctx.rotate(m.end === "a" ? -Math.PI / 2 : Math.PI / 2);
          ctx.font = `700 ${Math.max(1, 9 * pxPerM)}px Helvetica, Arial, sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(m.text ?? "", 0, 0);
        } else {
          ctx.fillRect((-m.l / 2) * pxPerM, (-m.w / 2) * pxPerM, m.l * pxPerM, m.w * pxPerM);
        }
        ctx.restore();
      }
    }
    ctx.restore();
  }

  /* taxiway markings: the derived centreline, and the survey's own edge and stand linework in yellow */
  ctx.drawImage(pav.centre, 0, 0, W, H);
  /* the survey's linework is about two metres wide on the sheet, so it fades as the zoom goes past what it can honestly show */
  const lineAlpha = pxPerM > 6 ? 0 : pxPerM > 2 ? 1 - (pxPerM - 2) / 4 : 1;
  if (ovTaxi && lineAlpha > 0) { ctx.save(); ctx.globalAlpha = (mode === "day" ? 0.85 : 0.3) * lineAlpha; ctx.drawImage(ovTaxi, 0, 0, W, H); ctx.restore(); }

  const assets = ASSETS as Rec[];
  /* holding positions */
  if (pxPerM > 0.08) {
    ctx.save();
    ctx.strokeStyle = pal.yellow;
    for (const h of assets.filter((a) => a.cat === "HOLD")) {
      const hb = holdBar(h, rws);
      if (!hb) continue;
      const [x, y] = S(hb.E, hb.N);
      if (!onScreen(x, y)) continue;
      const ang = Math.atan2(vN(hb.N + hb.uy) * H - y, uE(hb.E + hb.ux) * W - x);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(ang);
      const L = 23 * pxPerM;
      ctx.lineWidth = Math.max(0.6, 0.3 * pxPerM);
      ctx.setLineDash([]);
      for (const off of [-1.5, -0.6]) { ctx.beginPath(); ctx.moveTo(-L / 2, off * pxPerM); ctx.lineTo(L / 2, off * pxPerM); ctx.stroke(); }
      ctx.setLineDash([0.9 * pxPerM, 0.9 * pxPerM]);
      for (const off of [0.6, 1.5]) { ctx.beginPath(); ctx.moveTo(-L / 2, off * pxPerM); ctx.lineTo(L / 2, off * pxPerM); ctx.stroke(); }
      ctx.restore();
    }
    ctx.restore();
  }

  /* stands: the number, and a parked aircraft on two stands in three — simulated traffic, as the engine's own animation does */
  const stands = assets.filter((a) => a.cat === "STAND");
  if (pxPerM > 0.1) {
    ctx.save();
    stands.forEach((st, i) => {
      const [x, y] = S(st.E, st.N);
      if (!onScreen(x, y)) return;
      /* nose away from the nearest runway */
      let best = rws[0], bd = Infinity;
      for (const r of rws) { const d = Math.abs((st.E - r.a[0]) * -r.uy + (st.N - r.a[1]) * r.ux); if (d < bd) { bd = d; best = r; } }
      const side = Math.sign((st.E - best.a[0]) * -best.uy + (st.N - best.a[1]) * best.ux) || 1;
      const nx = -best.uy * side, ny = best.ux * side;
      const ang = Math.atan2(vN(st.N + ny) * H - y, uE(st.E + nx) * W - x);
      if (i % 3 !== 0) drawAircraft(ctx, x, y, ang, i % 4 === 1 ? 60 : 37, pxPerM, mode);
      ctx.fillStyle = mode === "day" ? "#2B2D30" : "rgba(230,230,220,.55)";
      ctx.font = `700 ${Math.max(6, 3.5 * pxPerM)}px ui-monospace, Menlo, monospace`;
      ctx.textAlign = "center";
      ctx.fillText(String(st.name).replace(/^STAND\s*/i, ""), x, y + 6 * pxPerM + 8);
    });
    ctx.restore();
  }

  /* signs and navaids as they stand on the ground */
  if (pxPerM > 0.12) {
    ctx.save();
    for (const a of assets) {
      const [x, y] = S(a.E, a.N);
      if (!onScreen(x, y)) continue;
      if (a.cat === "SIGN") {
        const w = Math.max(4, 2.4 * pxPerM), h = Math.max(2.5, 0.9 * pxPerM);
        ctx.fillStyle = mode === "day" ? "#C8202A" : "#8A1A20";
        ctx.fillRect(x - w / 2, y - h / 2, w, h);
        ctx.fillStyle = mode === "day" ? "#FFFFFF" : "#F5F0D0";
        ctx.fillRect(x - w / 2 + 1, y - h / 2 + 1, Math.max(1, w * 0.35), Math.max(1, h - 2));
      } else if (a.cat === "NAVAID" && /LLZ|localiser/i.test(a.name)) {
        /* a localiser array: a row of elements across the runway axis */
        const r = rws.find((rw) => rw.name.includes(String(a.name).replace(/\D/g, "").slice(0, 2))) ?? rws[0];
        const ang = Math.atan2(vN(a.N + r.ux) * H - y, uE(a.E - r.uy) * W - x);
        ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
        ctx.fillStyle = mode === "day" ? "#B03030" : "#F2F5F7";
        for (let k = -6; k <= 6; k++) ctx.fillRect(k * 2.2 * pxPerM - 0.5 * pxPerM, -0.6 * pxPerM, Math.max(1, 1 * pxPerM), Math.max(1, 1.2 * pxPerM));
        ctx.restore();
      } else if (a.cat === "PAPI") {
        const s = Math.max(2, 1.4 * pxPerM);
        ctx.fillStyle = mode === "day" ? "#2A2D30" : "#0D0F11";
        ctx.fillRect(x - s / 2, y - s / 2, s, s);
      } else if (a.cat === "MET" && /wind/i.test(a.name)) {
        ctx.fillStyle = "#FF7A1A";
        ctx.beginPath(); ctx.moveTo(x, y - 3 * pxPerM); ctx.lineTo(x + 4 * pxPerM, y); ctx.lineTo(x, y + 1.5 * pxPerM); ctx.closePath(); ctx.fill();
      }
    }
    ctx.restore();
  }

  /* the night */
  if (mode === "night") paintLights(ctx, S, pxPerM, onScreen, rws, nowMs, assets);
  return true;
}

function drawAircraft(ctx: CanvasRenderingContext2D, x: number, y: number, ang: number, lenM: number, pxPerM: number, mode: "day" | "night") {
  const L = lenM * pxPerM;
  if (L < 3) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  ctx.fillStyle = mode === "day" ? "#E9EBE6" : "#3A3E42";
  ctx.strokeStyle = mode === "day" ? "rgba(40,42,44,.55)" : "rgba(0,0,0,.6)";
  ctx.lineWidth = 0.6;
  const span = L * 0.95, fus = L * 0.11;
  /* wings */
  ctx.beginPath(); ctx.moveTo(-L * 0.05, -fus / 2); ctx.lineTo(-L * 0.3, -span / 2); ctx.lineTo(-L * 0.38, -span / 2); ctx.lineTo(-L * 0.2, fus / 2 * -1); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-L * 0.05, fus / 2); ctx.lineTo(-L * 0.3, span / 2); ctx.lineTo(-L * 0.38, span / 2); ctx.lineTo(-L * 0.2, fus / 2); ctx.closePath(); ctx.fill(); ctx.stroke();
  /* tailplane */
  ctx.beginPath(); ctx.moveTo(-L * 0.42, 0); ctx.lineTo(-L * 0.5, -span * 0.18); ctx.lineTo(-L * 0.5, span * 0.18); ctx.closePath(); ctx.fill(); ctx.stroke();
  /* fuselage */
  ctx.beginPath(); ctx.moveTo(L * 0.5, 0); ctx.quadraticCurveTo(L * 0.5, fus / 2, L * 0.38, fus / 2); ctx.lineTo(-L * 0.48, fus / 2); ctx.lineTo(-L * 0.5, 0); ctx.lineTo(-L * 0.48, -fus / 2); ctx.lineTo(L * 0.38, -fus / 2); ctx.quadraticCurveTo(L * 0.5, -fus / 2, L * 0.5, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.restore();
}

function paintLights(
  ctx: CanvasRenderingContext2D,
  S: (E: number, N: number) => [number, number],
  pxPerM: number,
  onScreen: (x: number, y: number) => boolean,
  rws: RunwayGeom[],
  nowMs: number,
  assets: Rec[],
) {
  const rad = Math.max(0.8, Math.min(2.6, 0.9 * pxPerM));
  const halo = rad * 3;
  const dot = (x: number, y: number, colour: string, size = 1) => {
    ctx.fillStyle = colour;
    ctx.globalAlpha = 0.18;
    ctx.beginPath(); ctx.arc(x, y, halo * size, 0, 6.2832); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(x, y, rad * size, 0, 6.2832); ctx.fill();
  };
  /* apron floodlights: masts at the apron corners, a warm pool beneath each */
  ctx.save();
  for (const poly of (derived as Rec).aprons as [number, number][][]) {
    for (const [E, N] of poly) {
      const [x, y] = S(E, N);
      if (!onScreen(x, y)) continue;
      const R = 110 * pxPerM;
      const g = ctx.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, "rgba(255,220,150,.28)"); g.addColorStop(1, "rgba(255,220,150,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, R, 0, 6.2832); ctx.fill();
      dot(x, y, "#FFE9B0", 1.6);
    }
  }
  /* the runway inventory: centreline and edge white, thresholds green */
  for (const a of assets) {
    if (a.cat !== "AGL") continue;
    const [x, y] = S(a.E, a.N);
    if (!onScreen(x, y)) continue;
    const sys = String(a.system || "");
    dot(x, y, /threshold/i.test(sys) ? "#3FE07A" : "#FFF7E0", /edge/i.test(sys) ? 1.15 : 0.95);
  }
  /* the PAPI: two red, two white on slope */
  const papi = assets.filter((a) => a.cat === "PAPI");
  papi.forEach((a, i) => { const [x, y] = S(a.E, a.N); if (onScreen(x, y)) dot(x, y, i % 4 < 2 ? "#FF3B30" : "#FFFFFF", 1.5); });
  /* the derived taxiway lights */
  const d = derived as Rec;
  for (const [E, N] of d.twyEdge as [number, number][]) { const [x, y] = S(E, N); if (onScreen(x, y)) dot(x, y, "#3A7BFF", 1); }
  for (const [E, N] of d.twyCentre as [number, number][]) { const [x, y] = S(E, N); if (onScreen(x, y)) dot(x, y, "#3FE07A", 0.8); }
  /* the approach lighting, with the sequenced flashers running toward the threshold */
  const phase = Math.floor(nowMs / 90) % 24;
  for (const r of rws) for (const end of ["a", "b"] as const) {
    for (const l of approachLights(r, end)) {
      const [x, y] = S(l.E, l.N);
      if (!onScreen(x, y)) continue;
      if (l.seq != null && Math.abs(l.seq - phase) <= 0) { dot(x, y, "#FFFFFF", 2.6); continue; }
      dot(x, y, l.colour, l.size ?? 0.9);
    }
  }
  ctx.restore();
}
